// admin/src/features/preview/calibration.ts
//
// CSS pixels per real millimeter for the preview. Detected from the
// screen itself (./screenSize.ts); a manual bank-card match (the ruler
// button) overrides it on screens the table doesn't know. The override is
// stored in device pixels per millimeter, a property of the physical
// screen, so dividing by the current devicePixelRatio keeps it right when
// the page is zoomed.
import { useCallback, useEffect, useState } from 'react';
import { detectScreen, probeScreen } from './screenSize';

const STORAGE_KEY = 'calyard.admin.devicePxPerMm.v1';

export const CSS_PX_PER_MM_RANGE = { min: 2.5, max: 9 } as const;
export function clampCssPxPerMm(value: number): number {
  return Math.min(CSS_PX_PER_MM_RANGE.max, Math.max(CSS_PX_PER_MM_RANGE.min, value));
}

export function toDevicePxPerMm(cssPxPerMm: number, dpr: number): number {
  return cssPxPerMm * dpr;
}

export function toCssPxPerMm(devicePxPerMm: number, dpr: number): number {
  return devicePxPerMm / dpr;
}

function readStored(): number | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const value = raw ? Number(raw) : NaN;
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeStored(devicePxPerMm: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(devicePxPerMm));
  } catch {
    // Private mode: the calibration lasts for this page only.
  }
}

export function useCssPxPerMm(): { cssPxPerMm: number; screenName: string; save: (cssPxPerMm: number) => void } {
  const [stored, setStored] = useState<number | null>(() => readStored());
  const [, setTick] = useState(0);

  useEffect(() => {
    const onResize = () => setTick((t) => t + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const save = useCallback((cssPxPerMm: number) => {
    const device = toDevicePxPerMm(clampCssPxPerMm(cssPxPerMm), window.devicePixelRatio || 1);
    writeStored(device);
    setStored(device);
  }, []);

  const detected = detectScreen(probeScreen());
  if (stored) {
    return { cssPxPerMm: clampCssPxPerMm(toCssPxPerMm(stored, window.devicePixelRatio || 1)), screenName: 'Matched by hand', save };
  }
  return { cssPxPerMm: detected.cssPxPerMm, screenName: detected.name, save };
}
